import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import StarRating from '../../../components/StarRating';
import TrustBadge from '../../../components/TrustBadge';
import ReviewCard from '../../../components/ReviewCard';
import ratingsService from '../../../services/ratingsService';

const SellerRatingsPanel = ({ userId, userName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (userId) loadData();
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    const { data: result } = await ratingsService?.getUserReputationWithBadges(userId);
    setData(result);
    setLoading(false);
  };

  const filteredReviews = data?.reviews?.filter(r => activeTab === 'all' || r?.role === activeTab) || [];

  const ratingBreakdown = (reviews, role) => {
    const roleReviews = reviews?.filter(r => r?.role === role) || [];
    const counts = [5, 4, 3, 2, 1]?.map(star => ({
      star,
      count: roleReviews?.filter(r => r?.rating === star)?.length || 0
    }));
    return { counts, total: roleReviews?.length };
  };

  const sellerBreakdown = ratingBreakdown(data?.reviews, 'seller');
  const buyerBreakdown = ratingBreakdown(data?.reviews, 'buyer');

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Star" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Ratings & Reviews</h2>
              <p className="text-sm text-muted-foreground">{userName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-muted-foreground">Loading ratings...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Score Overview */}
            <div className="grid grid-cols-2 gap-4">
              {/* Seller Score */}
              <div className="bg-muted/10 border border-border rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Seller Rating</p>
                <div className="text-4xl font-bold text-foreground mb-1">
                  {data?.reputation?.seller_rating > 0 ? data?.reputation?.seller_rating?.toFixed(1) : '—'}
                </div>
                <StarRating
                  rating={data?.reputation?.seller_rating || 0}
                  size={16}
                  showValue={false}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {data?.reputation?.total_seller_reviews || sellerBreakdown?.total} reviews
                </p>
                {/* Bar breakdown */}
                <div className="mt-3 space-y-1">
                  {sellerBreakdown?.counts?.map(({ star, count }) => (
                    <div key={star} className="flex items-center space-x-2 text-xs">
                      <span className="text-muted-foreground w-4">{star}</span>
                      <div className="flex-1 bg-muted/30 rounded-full h-1.5">
                        <div
                          className="bg-yellow-400 h-1.5 rounded-full transition-all"
                          style={{ width: sellerBreakdown?.total > 0 ? `${(count / sellerBreakdown?.total) * 100}%` : '0%' }}
                        />
                      </div>
                      <span className="text-muted-foreground w-4">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buyer Score */}
              <div className="bg-muted/10 border border-border rounded-lg p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wide">Buyer Rating</p>
                <div className="text-4xl font-bold text-foreground mb-1">
                  {data?.reputation?.buyer_rating > 0 ? data?.reputation?.buyer_rating?.toFixed(1) : '—'}
                </div>
                <StarRating
                  rating={data?.reputation?.buyer_rating || 0}
                  size={16}
                  showValue={false}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  {data?.reputation?.total_buyer_reviews || buyerBreakdown?.total} reviews
                </p>
                {/* Bar breakdown */}
                <div className="mt-3 space-y-1">
                  {buyerBreakdown?.counts?.map(({ star, count }) => (
                    <div key={star} className="flex items-center space-x-2 text-xs">
                      <span className="text-muted-foreground w-4">{star}</span>
                      <div className="flex-1 bg-muted/30 rounded-full h-1.5">
                        <div
                          className="bg-success h-1.5 rounded-full transition-all"
                          style={{ width: buyerBreakdown?.total > 0 ? `${(count / buyerBreakdown?.total) * 100}%` : '0%' }}
                        />
                      </div>
                      <span className="text-muted-foreground w-4">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted/10 border border-border rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-foreground">{data?.reputation?.total_sales || 0}</div>
                <div className="text-xs text-muted-foreground">Total Sales</div>
              </div>
              <div className="bg-muted/10 border border-border rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-foreground">{data?.reputation?.total_purchases || 0}</div>
                <div className="text-xs text-muted-foreground">Purchases</div>
              </div>
              <div className="bg-muted/10 border border-border rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-success">{data?.reputation?.successful_trades || 0}</div>
                <div className="text-xs text-muted-foreground">Successful</div>
              </div>
            </div>

            {/* Trust Badges */}
            {data?.badges?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
                  <Icon name="Shield" size={16} className="text-primary" />
                  <span>Trust Badges</span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {data?.badges?.map(badge => (
                    <TrustBadge key={badge?.id} badgeType={badge?.badge_type} size="sm" />
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground flex items-center space-x-2">
                  <Icon name="MessageSquare" size={16} className="text-primary" />
                  <span>Reviews ({data?.reviews?.length || 0})</span>
                </h3>
                <div className="flex space-x-1">
                  {['all', 'seller', 'buyer']?.map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                        activeTab === tab
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted/20 text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              {filteredReviews?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="MessageSquare" size={32} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No reviews yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredReviews?.map(review => (
                    <ReviewCard key={review?.id} review={review} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerRatingsPanel;
